// lib/calendarItems.ts — THE ONE PLACE A DATED THING BECOMES A ROW.
//
// B4 (2026-09-24): Huan's ruling, walking Today on a phone — "should Today
// also show what's coming up this week?" Yes. Today's "Coming up" needs the
// same agenda/auditions/sessions/shoot-days/engagements assembly
// `HouseholdCalendar.tsx` already built (holds folded into the record they
// mark, a due date that has passed excluded, the modality-aware titles, the
// two-hop "whose is it" walks) — extracted here rather than rebuilt, so the
// two screens can never drift into disagreeing about what a given morning
// is. HouseholdCalendar.tsx now calls `loadCalendarItems` too; its own
// rendering, window (60 days) and behaviour are unchanged — this file is the
// same code moved, not rewritten.
import {
  listAgenda,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  listHouseholdPerformers,
  type HouseholdPerformer,
} from './api';
import { activeOnly } from './retired';
import { humanizeValue } from './fieldDisplay';
import { AA, LINK, auditionFallbackName, otherEnd } from './auditionSpread';

/** One line in the agenda.
 *
 * ⚑ ONE THING, ONE ROW. A hold and the thing it marks are the same fact told
 * twice, and the first cut of this screen told it twice: "held · Nightfall —
 * shoot day" sat directly above "5:40 AM · Nightfall", and a family read one
 * morning as two. So a hold over a dated record FOLDS INTO that record's row
 * as `marked`, and only a hold with no dated record under it on that day —
 * a block of outside dates, which is a range and not an appointment — gets a
 * row of its own.
 *
 * ⚑ AND THE TITLE HAS ONE SHAPE: `<project or character> — <what>`. Three
 * different orders shipped in the first cut ("Wren — callback", "Callback —
 * Wren", a bare "Wren") and a column of titles that do not agree is a column
 * you have to read rather than scan. */
export interface CalendarItem {
  id: string;
  /** Local calendar day, `YYYY-MM-DD` — sortable, and the grouping key. */
  day: string;
  /** Sort key inside a day. Standalone holds lead the day. */
  order: string;
  /** The time, or the word that replaces it — never a fabricated 00:00. */
  when: string;
  /** This row IS a hold. The word in the time column is "held". */
  isHold: boolean;
  /** This row's day is ALSO held, so the row wears the word as a mark under
   * its time. Set by folding, never by the record itself. */
  marked: boolean;
  /** `<project or character> — <what>`. */
  what: string;
  /** Whose it is and where it lives — the second line, always words. */
  sub: string;
  open: (() => void) | null;
  /** The record this row is about, so a hold can find it and fold. */
  subjectId: string | null;
}

/** The grouping key is a real local date, so a window that crosses a year
 * boundary still orders December before January. */
export function dayKeyOf(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** The local day a datetime falls on, or null when the value is not one.
 * A DATE-shaped value (`2026-09-11`) is taken verbatim rather than parsed as
 * an instant — `new Date('2026-09-11')` is UTC midnight, which is the
 * previous day west of Greenwich, and a call sheet a day early is the exact
 * failure this pack cannot afford. */
export function dayOf(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const bare = /^(\d{4}-\d{2}-\d{2})$/.exec(v.trim());
  if (bare) return bare[1]!;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : dayKeyOf(d);
}

/** The clock time on a datetime, or null. Null renders a word instead: a
 * record with no time yet is still worth showing, and inventing one would be
 * a precise, confident, false statement about somebody's morning. */
export function timeOf(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "Today", "Tomorrow", then the weekday and the date. The two named days
 * are not decoration — they are the two a parent is actually reading for. */
export function dayLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const day = new Date(y!, m! - 1, d!);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const away = Math.round((day.getTime() - today.getTime()) / 86_400_000);
  if (away === 0) return 'Today';
  if (away === 1) return 'Tomorrow';
  return day.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

/** The far end of a span, said once on the day it begins. */
export function throughLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "Today" / "Tomorrow" / "Thu Sep 26" — B4's own format, distinct from
 * `dayLabel` above (which spells the weekday in full): a row on a list that
 * carries no separate date heading of its own has to say both words in one
 * short phrase. MOVED HERE FROM HouseholdHome.tsx (B13) so a second caller —
 * `lib/proposals.ts`'s audition line — reads the same formatter Today's
 * "Coming up" always has, rather than growing a second one that drifts. */
export function comingUpDayLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const day = new Date(y!, m! - 1, d!);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const away = Math.round((day.getTime() - today.getTime()) / 86_400_000);
  if (away === 0) return 'Today';
  if (away === 1) return 'Tomorrow';
  return day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** The pieces of a sub line, joined without a dangling separator when the
 * first of them is absent — the A4 lesson, verbatim: a captured audition has
 * no performer yet, and the row opened on a leading dot. */
export function joinParts(parts: (string | null)[]): string {
  return parts.filter((p): p is string => Boolean(p && p.trim())).join(' · ');
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** The doors a caller wants wired onto the rows it can open. Both optional —
 * Today reads this same list to show what's coming up without opening
 * anything from it, so every row it gets back simply carries `open: null`. */
export interface CalendarItemHandlers {
  onOpenAudition?: (id: string) => void;
  onOpenShootDay?: (id: string) => void;
}

/** Every dated thing a tenant owns between `from` and `to` (inclusive, by
 * local day), assembled exactly as `HouseholdCalendar.tsx` always has:
 * agenda invites, auditions (by their due date), sessions, shoot days and
 * the holds that mark them — with holds folded into the record they mark on
 * the day they coincide, and left standing alone only for a genuine span (a
 * block of outside dates). Soonest first.
 *
 * Throws on any read failure — the caller decides how to say so; this
 * function makes no rendering decisions beyond the item shapes above. */
export async function loadCalendarItems(
  tenantId: string,
  from: Date,
  to: Date,
  handlers: CalendarItemHandlers = {}
): Promise<CalendarItem[]> {
  const firstDay = dayKeyOf(from);
  const lastDay = dayKeyOf(to);
  const { onOpenAudition, onOpenShootDay } = handlers;

  const [agenda, auds, sessions, days, engagements, roles, projects] = await Promise.all([
    listAgenda(tenantId, { from: from.toISOString(), to: to.toISOString() }),
    listAllRecordsWithLinks(tenantId, AA.AUDITION, {
      linkTypes: [LINK.AUDITION_ROLE, LINK.AUDITION_SUBJECT],
    }),
    listAllRecordsWithLinks(tenantId, AA.SESSION, { linkTypes: [LINK.SESSION_AUDITION] }),
    listAllRecordsWithLinks(tenantId, AA.SHOOT_DAY, { linkTypes: [LINK.SHOOT_DAY_ENGAGEMENT] }),
    listAllRecordsWithLinks(tenantId, AA.ENGAGEMENT, {
      linkTypes: [LINK.ENGAGEMENT_ROLE, LINK.ENGAGEMENT_SUBJECT],
    }),
    listAllRecordsWithLinks(tenantId, AA.ROLE, { linkTypes: [LINK.ROLE_PROJECT] }),
    listAllRecordsByType(tenantId, AA.PROJECT),
  ]);

  let performers: HouseholdPerformer[] = [];
  try {
    performers = await listHouseholdPerformers(tenantId);
  } catch {
    // A child or a helper is REFUSED the household roster (aa_subject sits
    // at min_role_read 'staff'), and that refusal is an answer, not a
    // fault: her own dates still render, without a name beside each one
    // that she already knows. Every other read here still throws and is
    // reported loudly by the caller.
    performers = [];
  }

  const performerName = new Map(performers.map((p) => [p.id, p.name]));
  const roleRows = activeOnly(roles.records);
  const roleById = new Map(roleRows.map((r) => [r.id, r]));
  const projectById = new Map(activeOnly(projects).map((p) => [p.id, p]));
  const auditionRows = activeOnly(auds.records);
  const auditionById = new Map(auditionRows.map((a) => [a.id, a]));
  const engagementById = new Map(activeOnly(engagements.records).map((e) => [e.id, e]));

  /** The character a role names, falling back to the role's own name. */
  const characterOf = (roleId: string | null): string | null => {
    if (!roleId) return null;
    const role = roleById.get(roleId);
    if (!role) return null;
    return text(role.fields.character_name) ?? text(role.display_name);
  };
  /** The production a role belongs to. */
  const productionOf = (roleId: string | null): string | null => {
    if (!roleId) return null;
    const projectId = otherEnd(roles.linksByRecordId[roleId] ?? [], roleId, LINK.ROLE_PROJECT);
    const project = projectId ? projectById.get(projectId) : null;
    if (!project) return null;
    return text(project.fields.title) ?? text(project.display_name);
  };
  const whoseAudition = (auditionId: string): string | null => {
    const sid = otherEnd(auds.linksByRecordId[auditionId] ?? [], auditionId, LINK.AUDITION_SUBJECT);
    return sid ? performerName.get(sid) ?? null : null;
  };
  const roleOfAudition = (auditionId: string): string | null =>
    otherEnd(auds.linksByRecordId[auditionId] ?? [], auditionId, LINK.AUDITION_ROLE);
  /** shoot day → engagement, the master_detail hop the far side uses. */
  const engagementOfShootDay = (shootDayId: string): string | null =>
    otherEnd(days.linksByRecordId[shootDayId] ?? [], shootDayId, LINK.SHOOT_DAY_ENGAGEMENT);
  const roleOfEngagement = (engagementId: string | null): string | null =>
    engagementId
      ? otherEnd(engagements.linksByRecordId[engagementId] ?? [], engagementId, LINK.ENGAGEMENT_ROLE)
      : null;
  /** A called day is about whoever the engagement is about — two hops, the
   * same walk the pack's own hold reaction takes. */
  const whoseShootDay = (shootDayId: string): string | null => {
    const engagementId = engagementOfShootDay(shootDayId);
    if (!engagementId || !engagementById.has(engagementId)) return null;
    const sid = otherEnd(engagements.linksByRecordId[engagementId] ?? [], engagementId, LINK.ENGAGEMENT_SUBJECT);
    return sid ? performerName.get(sid) ?? null : null;
  };

  const out: CalendarItem[] = [];
  /** `<record id>|<day>` → the row already standing for it, so a hold over
   * that record on that day folds in rather than doubling it. */
  const byRecordDay = new Map<string, CalendarItem>();
  const add = (entry: CalendarItem): void => {
    out.push(entry);
    if (entry.subjectId) byRecordDay.set(`${entry.subjectId}|${entry.day}`, entry);
  };
  const inWindow = (day: string | null): day is string => day !== null && day >= firstDay && day <= lastDay;

  // ── AUDITIONS: the date the tape is owed by ──
  // A due date that has already passed is not on the calendar. It is on
  // Today, in the triage list, as the overdue thing it became — and putting
  // it in both places would make the calendar a second inbox.
  for (const audition of auditionRows) {
    // B15 — a tape already SENT is not still owed. `requested` is the
    // pack's only pre-submission stage (B13, decision `2d0f7662`); anything
    // else means a tape went, and this row would otherwise keep saying
    // "self-tape due" about a deadline that was already met.
    if (audition.lifecycle_state && audition.lifecycle_state !== 'requested') continue;
    const day = dayOf(audition.fields.due_at);
    if (!inWindow(day)) continue;
    const roleId = roleOfAudition(audition.id);
    const at = timeOf(audition.fields.due_at);
    const modality = text(audition.fields.modality);
    // A TAPE IS DUE; A ROOM IS SCHEDULED. The modality enters the title only
    // where it names the thing being handed in — "in-person due" would be
    // describing a deadline nobody has.
    const owed = modality === 'self_tape' ? 'self-tape due' : 'due';
    add({
      id: `due-${audition.id}`,
      day,
      order: `1${at ?? ''}`,
      when: at ?? 'due',
      isHold: false,
      marked: false,
      // B15 — never the record's own raw display_name (`{modality}`,
      // recordTypes.ts — an unhumanized value like `self_tape`); see
      // auditionFallbackName's own comment.
      what: `${characterOf(roleId) ?? auditionFallbackName(audition)} — ${owed}`,
      sub: joinParts([
        whoseAudition(audition.id),
        modality && modality !== 'self_tape' ? humanizeValue(modality) : null,
        'audition',
      ]),
      open: onOpenAudition ? () => onOpenAudition(audition.id) : null,
      subjectId: audition.id,
    });
  }

  // ── SESSIONS: a sitting somebody scheduled ──
  // A session has no screen of its own in this pack; it lives on the
  // audition it belongs to, which is where the row opens.
  for (const session of activeOnly(sessions.records)) {
    const day = dayOf(session.fields.scheduled_at);
    if (!inWindow(day)) continue;
    const auditionId = otherEnd(sessions.linksByRecordId[session.id] ?? [], session.id, LINK.SESSION_AUDITION);
    const character = auditionId ? characterOf(roleOfAudition(auditionId)) : null;
    const kindRaw = text(session.fields.session_kind);
    // Lower case after the dash, which is the shape the pack's own hold
    // labels already take ("Wren — callback"). The two must agree: when a
    // hold folds into this row they are the same sentence.
    const kindWords = kindRaw ? kindRaw.replace(/_/g, ' ') : 'session';
    const at = timeOf(session.fields.scheduled_at);
    const modality = text(session.fields.modality);
    add({
      id: `session-${session.id}`,
      day,
      order: `1${at ?? ''}`,
      when: at ?? 'session',
      isHold: false,
      marked: false,
      what: character ? `${character} — ${kindWords}` : kindWords.charAt(0).toUpperCase() + kindWords.slice(1),
      sub: joinParts([
        auditionId ? whoseAudition(auditionId) : null,
        modality ? humanizeValue(modality) : null,
        text(session.fields.location_or_link),
        session.fields.travel_needed === true ? 'travel' : null,
      ]),
      open: auditionId && onOpenAudition ? () => onOpenAudition(auditionId) : null,
      subjectId: session.id,
    });
  }

  // ── SHOOT DAYS: the one time of day somebody else decided ──
  for (const shootDay of activeOnly(days.records)) {
    const day = dayOf(shootDay.fields.shoot_date);
    if (!inWindow(day)) continue;
    const roleId = roleOfEngagement(engagementOfShootDay(shootDay.id));
    // THE ONE NUMBER OF THE MORNING is the pickup — the morning starts when
    // the car comes. Today's band and the shoot-day card both lead with it;
    // this row must say the same time they do, or a family sees two numbers
    // for one morning. Then hair and makeup, then on set — the order the
    // morning actually happens in.
    const call =
      timeOf(shootDay.fields.pickup_at) ?? timeOf(shootDay.fields.hair_makeup_at) ?? timeOf(shootDay.fields.on_set_at);
    const production = productionOf(roleId);
    add({
      id: `day-${shootDay.id}`,
      day,
      order: `1${call ?? ''}`,
      when: call ?? 'no call time yet',
      isHold: false,
      marked: false,
      what: production
        ? `${production} — shoot day`
        : `${text(shootDay.fields.day_label) ?? shootDay.display_name} — shoot day`,
      sub: joinParts([whoseShootDay(shootDay.id), text(shootDay.fields.day_label), text(shootDay.fields.location)]),
      open: onOpenShootDay ? () => onOpenShootDay(shootDay.id) : null,
      subjectId: shootDay.id,
    });
  }

  // ── INVITES: a dated thing that arrived by mail ──
  // Core's mail ingest records an .ics as a calendar event. It has no
  // household record behind it yet, so the row does not pretend to be a
  // door — it says who sent it and what it says.
  for (const event of agenda.events) {
    const day = dayOf(event.startLocal ?? event.startsAt);
    if (!inWindow(day)) continue;
    const at = event.startLocal ? event.startLocal.slice(11, 16) : timeOf(event.startsAt) ?? 'invited';
    add({
      id: `event-${event.id}`,
      day,
      order: `1${at}`,
      when: at,
      isHold: false,
      marked: false,
      what: event.title ?? 'An invitation with no subject line',
      sub: joinParts([event.organizer ? `from ${event.organizer}` : null, event.location, 'arrived by mail']),
      open: null,
      subjectId: null,
    });
  }

  // ── HOLDS: the marking, and the reason this screen exists ──
  //
  // ⚑ A SPAN IS NAMED ONCE AND CONTINUED THEREAFTER — the cockpit agenda's
  // own hard-won rule, and it is not a style choice: three promises over a
  // fortnight rendered as forty-two identical rows the first time, and the
  // agenda was unreadable. So: the full row on the day a hold starts, one
  // quiet continuation line per day after it, carrying the count. A middle
  // day must still LOOK spoken for.
  const continuing = new Map<string, number>();
  for (const hold of agenda.holds) {
    if (hold.status !== 'held') continue;
    // WHICH RECORD A HOLD LIVES ON. The pack writes exactly three
    // (actorAssistReactions.ts): outside dates on an audition, a callback on
    // its session, a called day on the shoot day. Anything else is another
    // vertical's hold on a shared primitive and is shown without a door
    // rather than guessed at.
    const auditionId =
      hold.subjectType === AA.AUDITION
        ? hold.subjectId
        : hold.subjectType === AA.SESSION
          ? otherEnd(sessions.linksByRecordId[hold.subjectId] ?? [], hold.subjectId, LINK.SESSION_AUDITION)
          : null;
    const shootDayId = hold.subjectType === AA.SHOOT_DAY ? hold.subjectId : null;
    const whose = auditionId ? whoseAudition(auditionId) : shootDayId ? whoseShootDay(shootDayId) : null;
    const livesOn =
      hold.subjectType === AA.SHOOT_DAY ? 'shoot day' : auditionId && auditionById.has(auditionId) ? 'audition' : null;
    const open = shootDayId
      ? onOpenShootDay
        ? () => onOpenShootDay(shootDayId)
        : null
      : auditionId && onOpenAudition
        ? () => onOpenAudition(auditionId)
        : null;

    if (hold.startsOn >= firstDay && hold.startsOn <= lastDay) {
      // ⚑ THE FOLD. A one-day hold over a record that is already on this day
      // IS that record's mark — the callback's own hold, the called day's
      // own hold. It becomes a word under that row's time rather than a
      // second row saying the same thing one line higher.
      //
      // A RANGE NEVER FOLDS. A block of outside dates is not an appointment:
      // nothing is scheduled inside it, and there is no row for it to
      // become. It keeps a row of its own, which is the one kind of hold
      // this screen exists to show in the first place.
      const under =
        hold.endsOn === hold.startsOn ? byRecordDay.get(`${hold.subjectId}|${hold.startsOn}`) : undefined;
      if (under) {
        under.marked = true;
      } else {
        add({
          id: `hold-${hold.id}`,
          day: hold.startsOn,
          order: '0',
          when: 'held',
          isHold: true,
          marked: false,
          what: hold.endsOn !== hold.startsOn ? `${hold.label} — through ${throughLabel(hold.endsOn)}` : hold.label,
          sub: joinParts([whose, livesOn]),
          open,
          subjectId: null,
        });
      }
    }
    // Every covered day after the first gets a continuation count.
    for (
      let d = new Date(`${hold.startsOn}T12:00:00`), first = true;
      dayKeyOf(d) <= hold.endsOn;
      d.setDate(d.getDate() + 1), first = false
    ) {
      const key = dayKeyOf(d);
      if (first || key < firstDay || key > lastDay) continue;
      continuing.set(key, (continuing.get(key) ?? 0) + 1);
    }
  }
  for (const [day, count] of continuing) {
    out.push({
      id: `holds-running-${day}`,
      day,
      order: '0z',
      when: 'held',
      isHold: true,
      marked: false,
      what: count === 1 ? 'A hold still running' : `${count} holds still running`,
      sub: 'named on the day it began',
      open: null,
      subjectId: null,
    });
  }

  out.sort((a, b) => (a.day + a.order < b.day + b.order ? -1 : 1));
  return out;
}
